import { HotelsList } from '@/components/hotels';
import { hotelCrudConfig, type Hotel } from '@/config/objects/hotels';
import { neighborhoodCrudConfig, type Neighborhood } from '@/config/objects/neighborhoods';
import {
  CrudDetailView,
  PageContainer,
  useApiClient,
  type CrudConfig,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Neighborhood detail page
 */
export function NeighborhoodDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [neighborhood, setNeighborhood] = React.useState<Neighborhood | null>(null);

  /**
   * Delete neighborhood mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (neighborhoodId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/neighborhoods/${neighborhoodId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        // Invalidate neighborhoods list to refresh data
        queryClient.invalidateQueries({ queryKey: ['Neighborhood'] });
        navigate('/neighborhoods');
      } else {
        throw new Error(response.error || 'Failed to delete neighborhood');
      }
    },
  });

  // Create custom config for neighborhood-specific hotels
  const neighborhoodHotelsConfig: CrudConfig<Hotel> = useMemo(
    () => ({
      ...hotelCrudConfig,
      entityName: 'Hotel',
      entityNamePlural: 'Hotels',
      endpoints: {
        ...hotelCrudConfig.endpoints,
        list: `/neighborhoods/${id}/hotels`,
        delete: (hotelId: string) => `/neighborhoods/${id}/hotels/${hotelId}`,
      },
      listView: {
        ...hotelCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/hotels',
            where: neighborhood?.greaterAreaId
              ? {
                  greaterAreaId: neighborhood.greaterAreaId,
                  neighborhoodId: {
                    not: neighborhood.id,
                  },
                }
              : neighborhood
                ? {
                    neighborhoodId: {
                      not: neighborhood.id,
                    },
                  }
                : undefined,
            onAdd: async (hotel: Hotel) => {
              await apiClient.post(`/neighborhoods/${id}/hotels`, { hotelId: hotel.id });
              toast.success('Hotel added to neighborhood');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (hotel: Hotel) => {
              await apiClient.delete(`/neighborhoods/${id}/hotels/${hotel.id}`);
              toast.success('Hotel removed from neighborhood');
            },
          },
        },
      },
    }),
    [neighborhood, id, apiClient],
  );

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Neighborhood Not Found</h2>
          <p className="text-muted-foreground">The requested neighborhood could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  /**
   * Navigate to edit form for this neighborhood
   */
  const handleEdit = (neighborhood: Neighborhood) => {
    navigate(`/neighborhoods/${neighborhood.id}/edit`);
  };

  /**
   * Handle neighborhood deletion
   */
  const handleDelete = async (neighborhood: Neighborhood) => {
    return toast.promise(deleteMutation.mutateAsync(neighborhood.id), {
      loading: 'Deleting neighborhood...',
      success: 'Neighborhood deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete neighborhood',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Neighborhoods', href: '/neighborhoods' },
    { label: neighborhood?.name || 'Neighborhood', current: true },
  ];

  return (
    <PageContainer>
      <div className="space-y-6">
        <CrudDetailView
          config={neighborhoodCrudConfig}
          itemId={id}
          breadcrumbs={breadcrumbs}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onData={(neighborhoodData: Neighborhood) => setNeighborhood(neighborhoodData)}
        />

        {/* Hotels Section */}
        <HotelsList config={neighborhoodHotelsConfig} className="p-0" baseUrl="/hotels" />
      </div>
    </PageContainer>
  );
}
